import ReactGA from 'react-ga4';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////////
import {
	PageTitle,
	HackCard,
	EnergyHack,
	MultiTap,
	EnergyTank,
	ExchangeDiamonds,
	Autoclicker,
	InsufficientGems,
} from '@/app/shared/components';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { setVisibleModalInsufficientGems } from '@/core/redux/slices';

/**
 * The Hacks page.
 *
 * This page displays all the hacks that the user can purchase.
 *
 * @returns The Hacks page.
 */
const Hacks: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();
	const dispatch = useCustomDispatch();

	/*********************
	// #region Redux
	**********************/
	const {
		availableEnergyHack,
		multiTapLevel,
		multiTapCostLevelUp,
		energyTankLevel,
		energyTankCostLevelUp,
		countdownEnergyHack,
		autoclickerTimerRemainingSeconds,
	} = useCustomSelector((state) => state.hacks);
	const { visibleModalInsufficientGems } = useCustomSelector(
		(state) => state.shop
	);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/hacks',
			title: 'Hacks',
		});
	}, []);

	/*********************
	// #region States
	**********************/
	const [visibleDrawerEnergyHack, setVisibleDrawerEnergyHack] =
		useState<boolean>(false);
	const [visibleDrawerMultiTap, setVisibleDrawerMultiTap] =
		useState<boolean>(false);
	const [visibleDrawerTank, setVisibleDrawerTank] = useState<boolean>(false);
	const [visibleExchangeDiamonds, setVisibleExchangeDiamonds] =
		useState<boolean>(false);
	const [visibleAutoclicker, setVisibleAutoclicker] =
		useState<boolean>(false);

	/*********************
	// #region Functions  
	**********************/
	const handleDrawer = (id: number) => {
		if (id === 1) {
			setVisibleDrawerEnergyHack(true);
		} else if (id === 2) {
			setVisibleDrawerMultiTap(true);
		} else if (id === 3) {
			setVisibleDrawerTank(true);
		} else if (id === 4) {
			setVisibleExchangeDiamonds(true);
		} else if (id === 5) {
			setVisibleAutoclicker(true);
		} else {
			return;
		}
	};

	/*********************
	// #region Template
	**********************/
	return (
		<>
			<div className="flex flex-col gap-2 pt-8 pb-32 mx-4">
				<PageTitle>{t('hacks.title')}</PageTitle>
				<div className="mb-4">
					<p className="mb-2">{t('hacks.dailyHacks')}</p>
					<div className="flex gap-4 justify-start">
						<HackCard
							name={t('hacks.energyHack.title')}
							id={1}
							image={process.env.VITE_ENERGY_IMAGE!}
							gradient={'bg-daily-hack-gradient'}
							value={availableEnergyHack}
							textValue={`${t('hacks.dailyHacks')} :`}
							type={'daily'}
							onClick={handleDrawer}
							isBlocked={countdownEnergyHack > 0}
							countdown={countdownEnergyHack}
						/>
					</div>
				</div>

				<div className="mb-4">
					<p className="mb-2">{t('hacks.title')}</p>
					<div className="flex justify-start gap-4">
						<HackCard
							name={t('hacks.multiTap.title')}
							id={2}
							image={process.env.VITE_MULTITAP_IMAGE!}
							gradient={'bg-hack-gradient'}
							type={'permanent'}
							imageText={process.env.VITE_COIN_IMAGE}
							cost={multiTapCostLevelUp}
							level={multiTapLevel + 1}
							onClick={handleDrawer}
						/>

						<HackCard
							name={t('hacks.energyTank.title')}
							id={3}
							image={process.env.VITE_TANK_IMAGE!}
							gradient={'bg-hack-gradient'}
							type={'permanent'}
							imageText={process.env.VITE_COIN_IMAGE}
							cost={energyTankCostLevelUp}
							level={energyTankLevel + 1}
							onClick={handleDrawer}
						/>
					</div>
				</div>

				<div className="mb-4">
					<p className="mb-2">{t('hacks.diamondTitle')}</p>
					<div className="flex justify-start gap-4">
						<HackCard
							name={t('hacks.exchangeDiamonds.title')}
							id={4}
							image={
								process.env.VITE_EXCHANGE_DIAMONDS_HACKS_IMAGE!
							}
							gradient={'bg-diamond-hacks-gradient'}
							type={'diamonds'}
							onClick={handleDrawer}
						/>

						<HackCard
							name={t('hacks.autoclicker.title')}
							id={5}
							image={process.env.VITE_AUTOCLICKER_IMAGE!}
							gradient={'bg-diamond-hacks-gradient'}
							type={'diamonds'}
							onClick={handleDrawer}
							isBlocked={autoclickerTimerRemainingSeconds > 0}
							countdown={autoclickerTimerRemainingSeconds}
						/>
					</div>
				</div>
			</div>

			<EnergyHack
				isOpen={visibleDrawerEnergyHack}
				onClose={setVisibleDrawerEnergyHack}
				image={process.env.VITE_ENERGY_IMAGE!}
			/>

			<EnergyTank
				isOpen={visibleDrawerTank}
				onClose={setVisibleDrawerTank}
				image={process.env.VITE_TANK_IMAGE!}
			/>

			<MultiTap
				isOpen={visibleDrawerMultiTap}
				onClose={setVisibleDrawerMultiTap}
				image={process.env.VITE_MULTITAP_IMAGE!}
			/>

			<ExchangeDiamonds
				isOpen={visibleExchangeDiamonds}
				onClose={setVisibleExchangeDiamonds}
				image={process.env.VITE_EXCHANGE_DIAMONDS_HACKS_IMAGE!}
			/>

			<Autoclicker
				isOpen={visibleAutoclicker}
				onClose={setVisibleAutoclicker}
				image={process.env.VITE_AUTOCLICKER_IMAGE!}
			/>

			<InsufficientGems
				isOpen={visibleModalInsufficientGems}
				onClose={() => {
					dispatch(setVisibleModalInsufficientGems(false));
				}}
			/>
		</>
	);
};

export default Hacks;
